import {
  Badge,
  Box,
  Button,
  CloseButton,
  Code,
  Divider,
  Drawer,
  Group,
  Indicator,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconChevronRight,
  IconDownload,
  IconFilter,
  IconPlayerPlay,
  IconSearch,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type EvidenceKind,
  useAcknowledgeIssue,
  useAssignIssue,
  useBatchIssues,
  useExportIssues,
  useResolveIssue,
  useValidateBatch,
  useValidationStatus,
} from "../../api/hooks";
import type { BatchDetail, Issue, IssueStatus, RecommendedAction } from "../../api/types";
import { useHasRole, useUser } from "../../auth";
import { DetailRow } from "../../components/DetailRow";
import { DisabledReasonTooltip } from "../../components/DisabledReasonTooltip";
import { ReasonModal } from "../../components/ReasonModal";
import { type Severity, SeverityChip } from "../../components/SeverityChip";
import { LastValidatedText, ValidationStaleAlert } from "../../components/ValidationFreshness";
import { formatPacificDisplay } from "../../lib/datetime";
import { downloadUrl } from "../../lib/download";
import { notifyDownloadBlocked } from "../../lib/downloadNotify";
import { formatApiError } from "../../lib/errors";
import { invalidateBatchData, refreshInBackground } from "../../lib/invalidate";
import { queryKeys } from "../../lib/queryKeys";
import { VALIDATION_RUNNING_REASON, runningRunIdFrom } from "../../lib/runStages";
import { type SettledBaseline, observeSettled } from "../../lib/settledRun";
import { ResolveIssueModal } from "./ResolveIssueModal";

type SeverityFilter = "all" | Severity;

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  informational: 3,
};

const SEVERITY_LEFT_COLOR: Record<Severity, string> = {
  critical: "var(--mantine-color-red-6)",
  high: "var(--mantine-color-orange-5)",
  medium: "var(--mantine-color-yellow-5)",
  informational: "var(--mantine-color-gray-4)",
};

/** Human labels for the BE's RecommendedAction enum. */
const RECOMMENDED_ACTION_LABEL: Record<RecommendedAction, string> = {
  investigate: "Investigate",
  substitute: "Apply substitution",
  approve_exclusion: "Approve exclusion",
  recalibrate: "Recalibrate",
  reupload: "Re-upload",
  no_action: "No action needed",
};

export function IssuesTab({ batch }: { batch: BatchDetail }) {
  const issuesQ = useBatchIssues(batch.batch_id);
  const issues = useMemo(() => issuesQ.data?.items ?? [], [issuesQ.data]);
  const exportIssues = useExportIssues();
  const validate = useValidateBatch();
  // Whether the rows below still describe the batch's current data. The verdict
  // is the backend's — the same one sign-off refuses on — so the banner here and
  // the 422 there can never disagree.
  const validationQ = useValidationStatus(batch.batch_id);
  const queryClient = useQueryClient();
  // Re-running validation is a write (auditor-forbidden server-side); the FE
  // hides the button for read-only roles.
  const canValidate = useHasRole("operations", "reviewer", "admin");
  const [filter, setFilter] = useState("");
  const [severity, setSeverity] = useState<SeverityFilter>("all");
  const [selected, setSelected] = useState<Issue | null>(null);
  const [validateOpen, setValidateOpen] = useState(false);
  // Lets the modal's Cancel abort an in-flight validate (BUG-3b) — validation
  // can hang on an unreachable DUT server, so the user needs an escape hatch.
  const validateAbort = useRef<AbortController | null>(null);

  // POST /batches/{id}/validate — the data-quality rule engine runs in the
  // background (the DUT fan-out is slow / can be unreachable), so this returns a
  // 202 ack, not the run counts. `useValidationStatus` polls from there: it
  // reports the run's stage while it works and invalidates the batch surface
  // when it settles, which is why there are no blind delayed re-fetches here any
  // more. Reason is user-entered via ReasonModal (audit-critical).
  const onConfirmValidate = async (reason: string) => {
    const controller = new AbortController();
    validateAbort.current = controller;
    try {
      await validate.mutateAsync({
        batchId: batch.batch_id,
        reason,
        signal: controller.signal,
      });
      notifications.show({
        title: "Validation started",
        message:
          "Running in the background — progress appears above, and new findings " +
          "will land here when it finishes. Any that the corrected data resolved " +
          "will close automatically.",
        color: "blue",
        autoClose: 5000,
      });
      setValidateOpen(false);
      // After the close, never in front of it: the 202 is the whole outcome the
      // operator is waiting on, and the refresh below is housekeeping.
      refreshInBackground(invalidateBatchData(queryClient, batch.batch_id));
    } catch (err) {
      // A run was already in flight. There is nothing to retry and nothing went
      // wrong — attach to the one that is running rather than showing an error.
      if (runningRunIdFrom(err) !== null) {
        // Adopting is a success, so the handled 409 must not stay on the
        // mutation: it would resurface the next time the modal is opened and
        // read as a refusal that is not happening.
        validate.reset();
        notifications.show({
          title: "Validation already running",
          message: "A run is already in progress for this batch — showing that one.",
          color: "blue",
          autoClose: 5000,
        });
        setValidateOpen(false);
        refreshInBackground(invalidateBatchData(queryClient, batch.batch_id));
        return;
      }
      // A timeout / server error surfaces inline in the ReasonModal via the
      // mutation's error; a user-initiated Cancel (abort) is silent.
    } finally {
      validateAbort.current = null;
    }
  };

  // Say so when the run settles. The status poll already invalidates the issues
  // list on the same transition; this is the part the operator sees, and it is
  // the only completion signal there is — the 202 came back minutes earlier.
  const lastRun = validationQ.data?.last_run;
  // A run whose process died keeps `status: "running"` on the row — the backend
  // has no writer left to change it, which is the whole reason the verdict lives
  // in the top-level `abandoned` flag instead. So settledness cannot be read
  // from the status alone, or the one outcome with no other completion signal
  // would be the one that never announces itself.
  const abandoned = validationQ.data?.abandoned === true;
  const settled = lastRun && (lastRun.status !== "running" || abandoned) ? lastRun : null;
  // One value behind both triggers: the `disabled` prop and the sentence that
  // explains it read the same expression, so the gate and its explanation cannot
  // drift. `running` is heartbeat-measured server-side and is false once a run is
  // `abandoned`, so a wedged run re-enables the button rather than sealing it.
  const validateBlocked = validationQ.data?.running === true ? VALIDATION_RUNNING_REASON : null;
  // What was already finished when this tab mounted — see `observeSettled`
  // for why the baseline is taken even when nothing is terminal yet.
  const announced = useRef<SettledBaseline | null>(null);
  useEffect(() => {
    const { baseline, isNew } = observeSettled(
      announced.current,
      batch.batch_id,
      validationQ.data === undefined ? undefined : (settled?.id ?? null),
    );
    announced.current = baseline;
    if (!isNew || !settled) return;
    if (abandoned) {
      // Its own copy, not the failure copy: nothing is wrong with the batch's
      // data, the run's process went away. Telling the operator it failed would
      // send them looking for a problem that is not there.
      notifications.show({
        title: "Validation interrupted",
        message: "That run stopped before it finished — no findings were recorded. Run it again.",
        color: "orange",
        autoClose: 8000,
      });
      return;
    }
    notifications.show(
      settled.status === "completed"
        ? {
            title: "Validation finished",
            message: `${settled.findings ?? 0} finding(s) — the list below is up to date.`,
            color: "teal",
            autoClose: 6000,
          }
        : {
            title: "Validation failed",
            message: settled.last_error ?? "No findings were recorded. Run it again.",
            color: "red",
            autoClose: 8000,
          },
    );
  }, [settled, abandoned, validationQ.data, batch.batch_id]);

  const filtered = useMemo<Issue[]>(() => {
    const q = filter.trim().toLowerCase();
    return issues
      .filter((i) => {
        if (severity !== "all" && i.severity !== severity) return false;
        if (!q) return true;
        return (
          i.source_identifier.toLowerCase().includes(q) ||
          i.family.includes(q) ||
          i.description.toLowerCase().includes(q) ||
          i.recommended_action.includes(q)
        );
      })
      .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  }, [issues, filter, severity]);

  // The export only sends server-side filters (severity, status, etc.) —
  // the free-text "filter" input is client-only and not part of the
  // contract. Count what'll actually be exported so the button label
  // matches the resulting CSV row count.
  const exportable = useMemo<Issue[]>(
    () => issues.filter((i) => severity === "all" || i.severity === severity),
    [issues, severity],
  );

  const counts = useMemo(() => {
    const c: Record<Severity | "all", number> = {
      all: issues.length,
      critical: 0,
      high: 0,
      medium: 0,
      informational: 0,
    };
    for (const i of issues) c[i.severity]++;
    return c;
  }, [issues]);

  // POST /issues/export (per API_CONTRACT §12.1). Server returns a signed
  // URL (and SHA + row_count); the browser navigates to it to download.
  // The server records the audit row — no client-side CSV building.
  //
  // Note: the text-search input (`filter` state) is a client-side
  // convenience that filters the visible table. The contract has no
  // free-text export filter, so the server-side export will include rows
  // that the text search hides. The button label surfaces this when text
  // is active so the operator knows what they're getting.
  const onExport = async () => {
    try {
      const result = await exportIssues.mutateAsync({
        filter: {
          batch_id: batch.batch_id,
          // The UI exposes one severity at a time; the contract takes
          // an array, so we wrap before sending.
          severity: severity === "all" ? undefined : [severity],
          // status / family / source / period filters will land when
          // the UI exposes them — the typed shape supports arrays for
          // each, ready for the next iteration.
        },
        reason: `CSV export of issues for batch ${batch.batch_id}${filter.trim() ? " (text search not applied server-side)" : ""}`,
      });
      // Trigger the download. `url` is valid for ~60 seconds per §12.1.
      downloadUrl(result.url, `issues_${batch.batch_id}.csv`, {
        onBlocked: () => notifyDownloadBlocked("Issues export"),
      });
      notifications.show({
        title: "Export ready",
        message: `${result.row_count} row${result.row_count === 1 ? "" : "s"} · expires ${new Date(result.expires_at).toLocaleTimeString()}`,
        color: "teal",
        autoClose: 3500,
      });
    } catch (err) {
      notifications.show({
        title: "Export failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Title order={4}>Data quality issues</Title>
          <Group gap="xs">
            {canValidate ? (
              // Disabled while a run is in flight, not merely while the ack is
              // in flight: the backend refuses a second run anyway, and a
              // clickable button that always answers 409 is a worse answer than
              // one that shows the run is already happening. The tooltip is what
              // makes that answer legible — greyed-out on its own reads as a
              // broken app or a role problem, and it is neither.
              <DisabledReasonTooltip reason={validateBlocked} position="bottom-end">
                <Button
                  data-tour="issues-validate"
                  size="xs"
                  variant="default"
                  leftSection={<IconPlayerPlay size={14} />}
                  onClick={() => setValidateOpen(true)}
                  loading={validate.isPending}
                  disabled={validateBlocked !== null}
                >
                  Run validation
                </Button>
              </DisabledReasonTooltip>
            ) : null}
            <Button
              data-tour="issues-export"
              size="xs"
              variant="light"
              leftSection={<IconDownload size={14} />}
              onClick={onExport}
              disabled={exportable.length === 0 || exportIssues.isPending}
              loading={exportIssues.isPending}
            >
              Export CSV · {exportable.length}
            </Button>
          </Group>
        </Group>
        <Text size="sm" c="dimmed">
          Click a row to view full details. Filters update the export.
        </Text>
        {validationQ.data ? <LastValidatedText status={validationQ.data} /> : null}
      </Stack>

      {validationQ.data ? (
        <ValidationStaleAlert
          status={validationQ.data}
          // The same trigger by another route, so it has to agree with the header
          // button — same gate, same sentence. The alert stays visible (the
          // findings ARE still stale) and keeps its button, disabled and saying
          // why, rather than dropping the only call to action it has.
          canRerun={canValidate}
          rerunBlockedReason={validateBlocked}
          onRerun={() => setValidateOpen(true)}
        />
      ) : null}

      <Paper p="sm" withBorder radius="md">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon variant="subtle" color="gray" size="sm">
              <IconFilter size={14} />
            </ThemeIcon>
            <SegmentedControl
              size="xs"
              value={severity}
              onChange={(v) => setSeverity(v as SeverityFilter)}
              data={[
                { value: "all", label: `All (${counts.all})` },
                { value: "critical", label: `Critical (${counts.critical})` },
                { value: "high", label: `High (${counts.high})` },
                { value: "medium", label: `Medium (${counts.medium})` },
                { value: "informational", label: `Info (${counts.informational})` },
              ]}
            />
          </Group>
          <TextInput
            placeholder="Search source, family, action…"
            leftSection={<IconSearch size={14} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            size="xs"
            w={{ base: "100%", sm: 320 }}
          />
        </Group>
      </Paper>

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table
            verticalSpacing="sm"
            horizontalSpacing="md"
            fz="sm"
            highlightOnHover
            stickyHeader
            miw={920}
          >
            <Table.Thead style={{ background: "var(--mantine-color-gray-0)" }}>
              <Table.Tr>
                <Table.Th style={{ width: 6 }} />
                <Table.Th style={{ width: 110 }}>Severity</Table.Th>
                <Table.Th style={{ width: 130 }}>Family</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th style={{ width: 200 }}>Opened (PT)</Table.Th>
                <Table.Th style={{ width: 130 }}>Status</Table.Th>
                <Table.Th style={{ width: 32 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((i) => (
                <Table.Tr
                  key={i.id}
                  onClick={() => setSelected(i)}
                  style={{ cursor: "pointer" }}
                  data-tour={`issue-row-${i.id}`}
                >
                  <Table.Td
                    style={{
                      padding: 0,
                      borderLeft: `3px solid ${SEVERITY_LEFT_COLOR[i.severity]}`,
                    }}
                  />
                  <Table.Td>
                    <SeverityChip severity={i.severity} />
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="default" size="sm">
                      {i.family}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={500}>
                          {i.source_identifier}
                        </Text>
                        {i.must_resolve ? (
                          <Badge color="red" variant="light" size="xs">
                            must resolve
                          </Badge>
                        ) : null}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {RECOMMENDED_ACTION_LABEL[i.recommended_action]} · {i.description}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td ff="monospace" fz="xs">
                    {formatPacificDisplay(i.opened_at)}
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={i.status} />
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="View details" openDelay={300}>
                      <ThemeIcon variant="subtle" color="gray" size="sm">
                        <IconChevronRight size={14} />
                      </ThemeIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        {filtered.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <ThemeIcon variant="light" color="gray" size={40} radius="xl">
              <IconAlertTriangle size={20} />
            </ThemeIcon>
            <Text c="dimmed" size="sm">
              {issues.length === 0
                ? "No issues recorded against this batch."
                : "No issues match the current filter."}
            </Text>
          </Stack>
        ) : null}
      </Paper>

      <IssueDrawer issue={selected} batchId={batch.batch_id} onClose={() => setSelected(null)} />

      <ReasonModal
        opened={validateOpen}
        title="Run validation"
        description={`Re-run the data-quality rules on ${batch.batch_id}. New findings are added (existing triage is preserved); recorded in the audit log.`}
        confirmLabel="Run validation"
        busy={validate.isPending}
        error={validate.error}
        onConfirm={onConfirmValidate}
        onAbort={() => validateAbort.current?.abort()}
        onClose={() => {
          setValidateOpen(false);
          validate.reset();
        }}
      />
    </Stack>
  );
}

function StatusBadge({ status }: { status: IssueStatus }) {
  const color = status === "resolved" ? "teal" : status === "acknowledged" ? "blue" : "gray";
  const dot = status === "open" ? "red" : status === "acknowledged" ? "blue" : "teal";
  return (
    <Group gap={6} wrap="nowrap">
      <Indicator color={dot} size={6} processing={status === "open"} />
      <Badge color={color} variant="light" size="sm">
        {status}
      </Badge>
    </Group>
  );
}

function IssueDrawer({
  issue,
  batchId,
  onClose,
}: {
  issue: Issue | null;
  batchId: string;
  onClose: () => void;
}) {
  const me = useUser();
  const canMutate = useHasRole("reviewer", "admin");
  const ack = useAcknowledgeIssue();
  const assign = useAssignIssue();
  const resolve = useResolveIssue();
  const queryClient = useQueryClient();
  const [resolveOpen, setResolveOpen] = useState(false);

  const inFlight = ack.isPending || assign.isPending || resolve.isPending;
  // BE returns the updated record on mutation; push it to cache and
  // invalidate the list so the table re-renders with the new status.
  //
  // Calibration readiness goes with it (BIO-309 review): resolving or overriding
  // a calibration finding is exactly what un-blocks "Send for approval", and
  // LifecycleActions is mounted above the tabs — invalidating only the issues key
  // left it disabled against a stale verdict until a reload or window refocus.
  const invalidate = () => {
    refreshInBackground(
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.batches.issues(batchId) }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.batches.calibrationReadiness(batchId),
        }),
      ]),
    );
  };
  const notifyError = (action: string) => (err: unknown) =>
    notifications.show({
      title: `${action} failed`,
      message: formatApiError(err),
      color: "red",
      autoClose: 5000,
    });
  const onAcknowledge = async () => {
    if (!issue) return;
    try {
      await ack.mutateAsync({
        issueId: issue.id,
        reason: `Acknowledge ${issue.source_identifier}`,
      });
      notifications.show({
        title: "Acknowledged",
        message: issue.source_identifier,
        color: "blue",
      });
      onClose();
      invalidate();
    } catch (err) {
      notifyError("Acknowledge")(err);
    }
  };
  const onAssignToMe = async () => {
    if (!issue || !me) return;
    try {
      await assign.mutateAsync({
        issueId: issue.id,
        assigneeUserId: me.id,
        reason: `Assign to ${me.email}`,
      });
      notifications.show({
        title: "Assigned to you",
        message: issue.source_identifier,
        color: "blue",
      });
      onClose();
      invalidate();
    } catch (err) {
      notifyError("Assign")(err);
    }
  };
  // BIO-308 #2: a must-resolve finding needs structured evidence and a reason,
  // collected in the modal below. Resolve used to be one click with an
  // auto-synthesized reason and no record of what fixed the condition.
  const onResolve = async (args: {
    reason: string;
    evidenceKind?: EvidenceKind;
    evidenceRef?: string;
  }) => {
    if (!issue) return;
    try {
      await resolve.mutateAsync({
        issueId: issue.id,
        resolutionNote: args.reason,
        reason: args.reason,
        ...(args.evidenceKind ? { evidenceKind: args.evidenceKind } : {}),
        ...(args.evidenceRef ? { evidenceRef: args.evidenceRef } : {}),
      });
      notifications.show({ title: "Resolved", message: issue.source_identifier, color: "teal" });
      setResolveOpen(false);
      onClose();
      invalidate();
    } catch (err) {
      notifyError("Resolve")(err);
      throw err;
    }
  };

  // Status-driven button availability — server enforces the same
  // transitions; the FE just hides the obviously-invalid ones.
  const canAcknowledge = !!issue && issue.status === "open";
  const canAssign = !!issue && issue.status !== "resolved" && issue.status !== "ignored";
  const canResolve = !!issue && issue.status !== "resolved" && issue.status !== "ignored";

  return (
    <Drawer
      opened={issue !== null}
      onClose={onClose}
      position="right"
      size="md"
      withCloseButton={false}
      padding="lg"
      transitionProps={{ duration: 180 }}
    >
      {issue ? (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="xs" wrap="wrap">
                <SeverityChip severity={issue.severity} />
                <Badge variant="default" size="sm">
                  {issue.family}
                </Badge>
                <StatusBadge status={issue.status} />
                {issue.must_resolve ? (
                  <Badge color="red" variant="light" size="sm">
                    must resolve
                  </Badge>
                ) : null}
              </Group>
              <Title order={4} mt={4}>
                {issue.source_identifier}
              </Title>
              <Text size="xs" c="dimmed" ff="monospace">
                {issue.id} · #{issue.seq}
              </Text>
            </Stack>
            <CloseButton onClick={onClose} aria-label="Close details" />
          </Group>

          <Divider />

          <DetailRow label="Description" value={issue.description} />
          <DetailRow
            label="Recommended action"
            value={RECOMMENDED_ACTION_LABEL[issue.recommended_action]}
          />
          <DetailRow label="Regulatory basis" value={issue.regulatory_basis} mono />
          <DetailRow label="Assignee" value={issue.current_assignee} mono />

          <Divider />

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Period
            </Text>
            <Code block>
              {issue.period_start ?? "(none)"}
              {"\n→ "}
              {issue.period_end ?? "(none)"}
            </Code>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Opened (PT)
            </Text>
            <Text size="sm" ff="monospace">
              {formatPacificDisplay(issue.opened_at)}
            </Text>
          </Stack>

          <Divider />

          <Group justify="flex-end" gap="xs">
            <Button
              size="xs"
              variant="default"
              onClick={onAcknowledge}
              loading={ack.isPending}
              disabled={!canMutate || !canAcknowledge || inFlight}
            >
              Acknowledge
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={onAssignToMe}
              loading={assign.isPending}
              disabled={!canMutate || !canAssign || !me || inFlight}
            >
              Assign to me
            </Button>
            <Button
              data-tour="issue-resolve"
              size="xs"
              onClick={() => setResolveOpen(true)}
              loading={resolve.isPending}
              disabled={!canMutate || !canResolve || inFlight}
            >
              Resolve
            </Button>
          </Group>

          {!canMutate ? (
            <Box mt="xs">
              <Text size="xs" c="dimmed">
                Acknowledge / assign / resolve require Compliance Reviewer or Admin role.
              </Text>
            </Box>
          ) : null}
        </Stack>
      ) : null}
      {issue ? (
        <ResolveIssueModal
          issue={issue}
          opened={resolveOpen}
          onClose={() => setResolveOpen(false)}
          onConfirm={onResolve}
          pending={resolve.isPending}
        />
      ) : null}
    </Drawer>
  );
}
